import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
  type GridRowModesModel,
  GridRowModes,
  DataGrid,
  type GridColDef,
  Toolbar,
  GridActionsCellItem,
  type GridEventListener,
  type GridRowId,
  type GridRowModel,
  GridRowEditStopReasons,
  type GridSlotProps,
} from '@mui/x-data-grid';

import type { OutsideSpeaker, EditableOutsideSpeaker } from '../../../../../Data/types';

type Props = {
  readonly?: boolean;
  value?: OutsideSpeaker[];
  onChange?: (value: OutsideSpeaker[] | null) => void;
  error?: boolean;
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: EditableOutsideSpeaker[]) => EditableOutsideSpeaker[]) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
  }
}

function EditToolbar(props: GridSlotProps['toolbar']) {
  const { setRows, setRowModesModel } = props;

  const handleClick = () => {
    const id = Date.now();
    setRows((oldRows) => [...oldRows, { id, name: '', title: '', organization: '', isNew: true }]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
    }));
  };

  return (
    <Toolbar>
      <Button color="inherit" startIcon={<AddIcon />} onClick={handleClick}>
        Add Speaker
      </Button>
    </Toolbar>
  );
}

export default function OutsideSpeakers({ value, ...props }: Readonly<Props>) {
  const theme = useTheme();
  const [rows, setRows] = React.useState(value ? value.map((speaker) => ({ ...speaker, isNew: false })) : []);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows?.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = {
      ...newRow,
      isNew: false,
      id: newRow.id,
      name: newRow.name || '',
      title: newRow.title || '',
      organization: newRow.organization || '',
    };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    props.onChange?.(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      editable: true,
      flex: 10,
      disableColumnMenu: true,
    },
    {
      field: 'title',
      headerName: 'Title',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
      editable: true,
      flex: 10,
      disableColumnMenu: true,
    },
    {
      field: 'organization',
      headerName: 'Organization',
      editable: true,
      type: 'string',
      flex: 10,
      disableColumnMenu: true,
    },
  ];

  if (!props.readonly) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 5,
      disableColumnMenu: true,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit && props;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon />}
              label="Save"
              style={{
                color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    });
  }

  return (
    <Box
      sx={{
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        hideFooter={true}
        processRowUpdate={processRowUpdate}
        showToolbar={!props.readonly}
        slots={{ toolbar: props.readonly ? undefined : EditToolbar }}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
        isCellEditable={(params) => !props.readonly && params.field !== 'actions'}
        sx={{
          borderColor: props.error ? 'error.main' : 'divider',
        }}
      />
    </Box>
  );
}
